import React from "react"

function ContextPill({ label, value, tone = "neutral" }) {
  const visual =
    tone === "danger"
      ? { bg: "#fef2f2", border: "#fecaca", color: "#991b1b" }
      : tone === "info"
        ? { bg: "#eff6ff", border: "#bfdbfe", color: "#1d4ed8" }
        : tone === "success"
          ? { bg: "#f0fdf4", border: "#bbf7d0", color: "#166534" }
          : { bg: "#f8fafc", border: "#e2e8f0", color: "#334155" }

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        minHeight: 28,
        padding: "0 9px",
        borderRadius: 999,
        background: visual.bg,
        border: `1px solid ${visual.border}`,
        color: visual.color,
        fontSize: 11.5,
        fontWeight: 850,
        whiteSpace: "nowrap"
      }}
    >
      <span style={{ color: "#64748b", fontWeight: 800 }}>{label}</span>
      <span>{value}</span>
    </div>
  )
}

function ContactItem({ label, value }) {
  if (!value) return null

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        minWidth: 0,
        fontSize: 12.5,
        color: "#475569",
        lineHeight: 1.25
      }}
    >
      <span
        style={{
          fontSize: 10.5,
          fontWeight: 900,
          textTransform: "uppercase",
          letterSpacing: 0.35,
          color: "#94a3b8"
        }}
      >
        {label}
      </span>

      <span
        style={{
          minWidth: 0,
          color: "#334155",
          fontWeight: 700,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }}
      >
        {value}
      </span>
    </div>
  )
}

export default function PatientHeaderCard({
  patientInitials,
  patientName,
  patientStatusTag,
  phone,
  email,
  nextVisit,
  lastVisit,
  summaryPending,
  patientPlans,
  formatMoney,
  formatDateTime,
  compact = false
}) {
  const plansCount = Array.isArray(patientPlans) ? patientPlans.length : 0
  const hasPending = Number(summaryPending || 0) > 0

  const safeStatusTag = patientStatusTag || {
    label: "Ficha activa",
    bg: "#eff6ff",
    color: "#1d4ed8",
    border: "#bfdbfe"
  }

  const dateValue = nextVisit || lastVisit || null
  const dateLabel = nextVisit ? "Próxima" : lastVisit ? "Última" : "Seguimiento"
  const dateText = dateValue ? formatDateTime(dateValue) : "Sin citas"

  return (
    <div
      style={{
        minWidth: 0,
        border: "1px solid #e2e8f0",
        borderRadius: 18,
        background: "#ffffff",
        boxShadow: "0 8px 18px rgba(15,23,42,0.04)",
        padding: compact ? 10 : 12,
        display: "grid",
        gap: compact ? 8 : 10,
        alignContent: "start"
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "auto minmax(0, 1fr)",
          gap: 11,
          alignItems: "center",
          minWidth: 0
        }}
      >
        <div
          style={{
            width: compact ? 44 : 48,
            height: compact ? 44 : 48,
            borderRadius: 15,
            background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
            color: "#ffffff",
            display: "grid",
            placeItems: "center",
            fontWeight: 950,
            fontSize: compact ? 16 : 17,
            boxShadow: "0 10px 20px rgba(37,99,235,0.20)",
            flexShrink: 0
          }}
        >
          {patientInitials}
        </div>

        <div style={{ minWidth: 0, display: "grid", gap: 6 }}>
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
              minWidth: 0
            }}
          >
            <div
              style={{
                minWidth: 0,
                fontSize: compact ? 20 : 22,
                fontWeight: 950,
                color: "#0f172a",
                lineHeight: 1.04,
                letterSpacing: "-0.03em",
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                maxWidth: "100%"
              }}
              title={patientName || "Paciente"}
            >
              {patientName || "Paciente"}
            </div>

            <span
              style={{
                padding: "5px 9px",
                borderRadius: 999,
                background: safeStatusTag.bg,
                color: safeStatusTag.color,
                border: `1px solid ${safeStatusTag.border}`,
                fontSize: 11.5,
                fontWeight: 850,
                whiteSpace: "nowrap"
              }}
            >
              {safeStatusTag.label}
            </span>
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              alignItems: "center",
              flexWrap: "wrap",
              minWidth: 0
            }}
          >
            <ContactItem label="Tel" value={phone || "Sin teléfono"} />
            <ContactItem label="Correo" value={email} />
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: 7,
          flexWrap: "wrap",
          minWidth: 0
        }}
      >
        <ContextPill
          label={dateLabel}
          value={dateText}
          tone={nextVisit ? "info" : "neutral"}
        />

        <ContextPill
          label="Pendiente"
          value={hasPending ? formatMoney(summaryPending) : "Al corriente"}
          tone={hasPending ? "danger" : "success"}
        />

        <ContextPill
          label="Planes"
          value={String(plansCount)}
          tone={plansCount > 0 ? "info" : "neutral"}
        />
      </div>
    </div>
  )
}
